import React from "react";

//App
import { SettingProps } from "../../../types/setting";
import { SettingFormContainer } from "./SettingFormContainer";
import SettingView from "./SettingView";

//Common
import {
    DataGrid,
    ConfirmationDialog,
    Snackbar,
    Drawer,
    Chip,

} from "../../common/VFX-UI";
import { MoreActionButton } from "../../common/Buttons";
import PageHeader from "../../common/PageHeader/PageHeader";
import MoreActionsMenu from "../../common/MoreActionsMenu/MoreActionsMenu";
import useSetting from "./hooks/useSetting";
import SearchContainer from "../../common/Forms/SearchContainer/SearchContainer";
import { SettingDto } from "../../../app/model/api/types/dto";
import { SearchProps } from "../../../types";
import { SearchDefaultValue } from "../../../store/types";
import { SearchFieldsDefaultValue } from "../../../utility/constants/constants";

const Settings: React.FC<SettingProps> = (props) => {
    const { data, activateMutation, deleteMutation } = props;
    const {
        state,
        formContext,
        rowEvents,
        actionColumnAnchorEl,
        toggleNewViewHandler,
        toggleViewHandler,
        toggleEditViewHandler,
        toggleActivateHandler,
        toggleDeactivateHandler,
        handleDeactivate,
        handleActivate,
        onShowSuccess,
        onAddSnackbarClose,
        handleActionClick,
        setActionColumnAnchorEl,
        handleActiveToggleChange,
    } = useSetting({
        deleteMutation: deleteMutation,
        activateMutation: activateMutation,
    });

    const openMenu = Boolean(actionColumnAnchorEl);

    const columns = [
        {
            dataField: "id",
            text: "ID",
            hidden: true,
        },
        {
            dataField: "name",
            text: "Name",
            sort: true,
        },
        {
            dataField: "description",
            text: "Description",
            sort: true,
        },
        {
            dataField: "code",
            text: "Code",
            sort: false,
        },
        {
            dataField: "value",
            text: "Value",
            sort: false,
        },
        {
            dataField: "isActive",
            text: "Status",
            sort: false,
            formatter: (cell: boolean) => {
                return <Chip label={cell ? "Active" : "Inactive"} />;
            },
        },
        {
            dataField: "action",
            text: "",
            style: { width: "25px" },
            isDummyField: true,
            formatter: () => {
                return (
                    <MoreActionButton handleActionClick={(e) => handleActionClick(e)} />
                );
            },
        },
    ];

    const searchProps: SearchProps<SettingDto> = {
        searchValue: formContext?.searchValue ?? SearchDefaultValue,
        searchFields: { ...SearchFieldsDefaultValue, name: true },
        setSearchValue: formContext?.setSearchValue,
        refetchGrid: formContext?.refetchGrid,
    };

    return (
        <>
            <>
                <PageHeader>Settings</PageHeader>
                <SearchContainer
                    searchProps={searchProps}
                    activeToggle={formContext?.activeToggle ?? true}
                    handleActiveToggleChange={handleActiveToggleChange}
                    toggleNewViewHandler={toggleNewViewHandler}
                />

            </>

            <Drawer show={state.toggleNewForm}>
                {state.toggleNewForm && (
                    <SettingFormContainer
                        onClose={toggleNewViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleEditForm}>
                {state.toggleEditForm && formContext?.data && (
                    <SettingFormContainer
                        onClose={toggleEditViewHandler}
                        onShowSuccess={onShowSuccess}
                    />
                )}
            </Drawer>

            <Drawer show={state.toggleView}>
                {state.toggleView && formContext?.data && (
                    <SettingView
                        onClose={toggleViewHandler}
                        editAction={() => {
                            toggleEditViewHandler();
                        }}
                        toggleDeactivateHandler={toggleDeactivateHandler}
                        toggleActivateHandler={toggleActivateHandler}
                    />
                )}
            </Drawer>

            <DataGrid
                columns={columns}
                data={data}
                keyField="id"
                rowEvents={rowEvents}
                totalSize={formContext?.paging?.totalSize}
                pageSize={formContext?.pageSize}
                remote={true}
                page={formContext?.paging?.currentPage}
                handlePageChange={async (newPage, pageSize ,sortField, sortOrder) => {
                    if (formContext?.setCurrentPage && newPage) {
                        await formContext?.setCurrentPage(newPage);
                    }
                    if (formContext?.setCurrentPageSize && pageSize) {
                        await formContext?.setCurrentPageSize(pageSize);
                    }
                    if (formContext?.setSortBy && sortField && sortOrder) {
                        await formContext?.setSortBy({
                            field: sortField,
                            direction: sortOrder === "asc" ? 1 : -1,
                        });
                    }
                    formContext?.refetchGrid();
                }}
            />

            {actionColumnAnchorEl && (
                <MoreActionsMenu
                    isOpen={openMenu}
                    target={actionColumnAnchorEl}
                    toggle={() => {
                        setActionColumnAnchorEl(null);
                    }}
                    onViewClick={() => {
                        toggleViewHandler();
                        setActionColumnAnchorEl(null);
                    }}
                />
            )}

            <ConfirmationDialog
                open={state.isDeactivateDialogOpen}
                onClose={toggleDeactivateHandler}
                onConfirm={handleDeactivate}
                contentTitle={`Deactivate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            ></ConfirmationDialog>

            <ConfirmationDialog
                open={state.isActivateDialogOpen}
                onClose={toggleActivateHandler}
                onConfirm={handleActivate}
                contentTitle={`Activate ${formContext?.data?.name}`}
                contentText={"Are you sure you want to continue?"}
                confirmText={"Yes, Continue"}
                closeText={"No, Go Back"}
            />

            {state.showSuccessSnackbar && (
                <Snackbar
                    open={state.showSuccessSnackbar}
                    message={state.successMessage}
                    handleClose={onAddSnackbarClose}
                    severity="success"
                />
            )}
        </>
    );
};

export default Settings;
